// Ponte card ↔ conversa: "Ver conversa" em qualquer card leva ao Atendimento
// já no contato certo, e o Atendimento mostra "Voltar para o card" — que
// reabre o card de onde a pessoa saiu. Junto com o rascunho guardado por
// conversa (conversationMemory.ts), ir e voltar não desfaz nada.
import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { toast } from 'sonner';
import { attendancesService } from '../services/attendances';
import { ApiError } from '../services/api';

const RETURN_KEY = 'sophia:atendimento:returnTo';

export interface ReturnTarget {
  /** Caminho de volta, já com ?card=<id> para reabrir o card. */
  path: string;
  /** Título do card, para o botão "Voltar para …". */
  label: string;
}

export function loadReturnTarget(): ReturnTarget | null {
  try {
    const raw = sessionStorage.getItem(RETURN_KEY);
    return raw ? (JSON.parse(raw) as ReturnTarget) : null;
  } catch {
    return null;
  }
}

export function clearReturnTarget(): void {
  try { sessionStorage.removeItem(RETURN_KEY); } catch { /* ignora */ }
}

function saveReturnTarget(target: ReturnTarget): void {
  try { sessionStorage.setItem(RETURN_KEY, JSON.stringify(target)); } catch { /* ignora */ }
}

export interface ConversationSource {
  session_id?: number | null;
  contact_phone?: string | null;
  /** Pedidos (FIN-, SEC-…) trazem o telefone aqui, não em contact_phone. */
  requester_phone?: string | null;
}

/** Texto do aviso quando o card não tem por onde chegar à conversa. */
export const NO_CONVERSATION_REASON =
  'Este card não tem telefone nem conversa de origem. Cadastre o telefone do responsável para abrir a conversa.';

/** Telefone do card: contato (leads, matrículas, pedidos da loja) ou solicitante (pedidos). */
export function conversationPhone(source: ConversationSource | null | undefined): string {
  return [source?.contact_phone, source?.requester_phone].find((p) => !!p?.replace(/\D/g, '')) ?? '';
}

/** O card tem por onde chegar à conversa (sessão de origem ou telefone)? */
export function canOpenConversation(source: ConversationSource | null | undefined): boolean {
  return !!source && (!!source.session_id || !!conversationPhone(source));
}

/** Abre a conversa do solicitante e guarda o caminho de volta ao card. */
export function useOpenConversation() {
  const navigate = useNavigate();
  const location = useLocation();

  return useCallback(
    async (source: ConversationSource | null | undefined, card: { id: string | number; title: string }) => {
      if (!source || !canOpenConversation(source)) {
        toast.info(NO_CONVERSATION_REASON);
        return;
      }
      let target: string;
      if (source.session_id) {
        // Confere o acesso antes de sair do card (sessão de outro setor → 403).
        try {
          await attendancesService.getSession(source.session_id);
        } catch (err) {
          toast.error(
            err instanceof ApiError && err.status === 403
              ? 'Você não tem acesso a esta conversa.'
              : 'Não foi possível abrir esta conversa.',
          );
          return;
        }
        target = `/atendimento/conversas?focusSessionId=${source.session_id}`;
      } else {
        target = `/atendimento/conversas?focusPhone=${encodeURIComponent(conversationPhone(source))}`;
      }
      const back = new URLSearchParams(location.search);
      back.set('card', String(card.id));
      saveReturnTarget({ path: `${location.pathname}?${back.toString()}`, label: card.title });
      navigate(target);
    },
    [navigate, location.pathname, location.search],
  );
}

// ── Card aberto ao sair da página ────────────────────────────────────────
// Um card por página. Quem sai de um card aberto por qualquer caminho (barra
// lateral, Alt+A, link) o encontra aberto de novo ao voltar àquela página
// (AppLayout → restoreLeftOpenCard) e vê "Voltar para o card" no Atendimento.
// Só fechar o card esquece.

const OPEN_CARDS_KEY = 'sophia:openCards';
const OPEN_CARD_TTL_MS = 8 * 60 * 60 * 1000;

interface OpenCardEntry extends ReturnTarget {
  leftAt: number | null;
  /** A pessoa dispensou o "Voltar para o card" — o card ainda reabre na página. */
  bannerDismissed?: boolean;
}

type OpenCards = Record<string, OpenCardEntry>;

function readOpenCards(): OpenCards {
  try {
    const raw = sessionStorage.getItem(OPEN_CARDS_KEY);
    const all = raw ? (JSON.parse(raw) as OpenCards) : {};
    const now = Date.now();
    for (const [page, entry] of Object.entries(all)) {
      if (entry.leftAt && now - entry.leftAt > OPEN_CARD_TTL_MS) delete all[page];
    }
    return all;
  } catch {
    return {};
  }
}

/** Mudou algo que a barra de abertos mostra (card aberto, conversa atual…). */
export const OPEN_TABS_EVENT = 'sophia:open-tabs-changed';

export function announceOpenTabsChange(): void {
  queueMicrotask(() => window.dispatchEvent(new Event(OPEN_TABS_EVENT)));
}

function writeOpenCards(all: OpenCards): void {
  try { sessionStorage.setItem(OPEN_CARDS_KEY, JSON.stringify(all)); } catch { /* ignora */ }
  announceOpenTabsChange();
}

/** Cards abertos ou deixados abertos, por página (barra de abertos). */
export function listOpenCards(): { page: string; path: string; label: string; leftAt: number | null }[] {
  return Object.entries(readOpenCards()).map(([page, e]) => ({ page, path: e.path, label: e.label, leftAt: e.leftAt }));
}

/** Esquece o card deixado aberto numa página (fechou a aba): não reabre mais e
 *  sai do "Voltar para o card". */
export function forgetOpenCard(page: string): void {
  const all = readOpenCards();
  const entry = all[page];
  if (!entry) return;
  delete all[page];
  writeOpenCards(all);
  const ret = loadReturnTarget();
  if (ret && sameCardPath(ret.path, entry.path)) clearReturnTarget();
}

function cardParamOf(path: string): string | null {
  return new URLSearchParams(path.split('?')[1] ?? '').get('card');
}

function sameCardPath(a: string, b: string): boolean {
  return a.split('?')[0] === b.split('?')[0] && cardParamOf(a) === cardParamOf(b);
}

/** Card deixado aberto mais recente vira o "Voltar para o card" do Atendimento.
 *  Chamar num efeito: o card só marca a saída ao desmontar, depois do render
 *  da página nova. */
export function takeLeftOpenCard(): ReturnTarget | null {
  const latest = Object.values(readOpenCards())
    .filter((e) => e.leftAt && !e.bannerDismissed)
    .sort((a, b) => (b.leftAt ?? 0) - (a.leftAt ?? 0))[0];
  if (!latest) return null;
  const target = { path: latest.path, label: latest.label };
  saveReturnTarget(target);
  return target;
}

/** "Dispensar" no Atendimento: some a faixa, mas o card continua aberto na página. */
export function dismissLeftOpenCardBanner(path: string): void {
  const all = readOpenCards();
  for (const entry of Object.values(all)) {
    if (sameCardPath(entry.path, path)) entry.bannerDismissed = true;
  }
  writeOpenCards(all);
}

/** Voltou a uma página onde deixou um card aberto (sem ?card na URL)? Devolve
 *  o caminho que o reabre. */
export function restoreLeftOpenCard(pathname: string, search: string): string | null {
  if (new URLSearchParams(search).has('card')) return null;
  const entry = readOpenCards()[pathname];
  if (!entry?.leftAt) return null;
  const params = new URLSearchParams(search);
  params.set('card', cardParamOf(entry.path) ?? '');
  return `${pathname}?${params.toString()}`;
}

/** Registra o card aberto (para voltar a ele) enquanto o painel estiver aberto. */
export function useRememberOpenCard(open: boolean, card: { id: string | number; title: string } | null | undefined) {
  const location = useLocation();
  const openRef = useRef(open);
  openRef.current = open;
  const pageRef = useRef(location.pathname);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    // A página do card aberto (o mesmo painel serve vários boards em /board/:sector).
    if (open && card) pageRef.current = location.pathname;
    const page = pageRef.current;
    if (open && card) {
      wasOpenRef.current = true;
      const back = new URLSearchParams(location.search);
      back.set('card', String(card.id));
      const path = `${page}?${back.toString()}`;
      const all = readOpenCards();
      all[page] = { path, label: card.title, leftAt: null };
      writeOpenCards(all);
      // De volta ao card: o "Voltar para o card" já cumpriu o papel.
      const ret = loadReturnTarget();
      if (ret && sameCardPath(ret.path, path)) clearReturnTarget();
      return;
    }
    // Só fechar (aberto → fechado) esquece; montar fechado não apaga o card
    // que esta página vai reabrir.
    if (!open && wasOpenRef.current) {
      wasOpenRef.current = false;
      const all = readOpenCards();
      const closed = all[page];
      delete all[page];
      writeOpenCards(all);
      const ret = loadReturnTarget();
      if (closed && ret && sameCardPath(ret.path, closed.path)) clearReturnTarget();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, card?.id, card?.title]);

  // Desmontou com o card ainda aberto = a pessoa foi para outra página.
  useEffect(() => () => {
    if (!openRef.current) return;
    const all = readOpenCards();
    const entry = all[pageRef.current];
    if (entry) {
      all[pageRef.current] = { ...entry, leftAt: Date.now(), bannerDismissed: false };
      writeOpenCards(all);
    }
  }, []);
}

// ── Alt+A ─────────────────────────────────────────────────────────────────
// Com um card aberto, Alt+A abre a conversa do solicitante; fora dele, vai
// para o Atendimento; no Atendimento, volta para onde a pessoa estava.

export const CONVERSATION_SHORTCUT_LABEL = 'Alt+A';

export function isConversationShortcut(e: KeyboardEvent): boolean {
  return e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'KeyA';
}

let activeCardConversation: (() => void) | null = null;

export function getActiveCardConversation(): (() => void) | null {
  return activeCardConversation;
}

/** O card aberto oferece o "Ver conversa" para o Alt+A. */
export function useRegisterCardConversation(handler: (() => void) | null) {
  const ref = useRef(handler);
  ref.current = handler;
  const enabled = !!handler;
  useEffect(() => {
    if (!enabled) return;
    const fn = () => ref.current?.();
    activeCardConversation = fn;
    return () => {
      if (activeCardConversation === fn) activeCardConversation = null;
    };
  }, [enabled]);
}

// Última página fora do Atendimento — o Alt+A no Atendimento volta para ela
// quando não há card para reabrir.
const LAST_PAGE_KEY = 'sophia:lastPageBeforeChat';

export function rememberPageBeforeChat(path: string): void {
  try { sessionStorage.setItem(LAST_PAGE_KEY, path); } catch { /* ignora */ }
}

export function loadPageBeforeChat(): string | null {
  try { return sessionStorage.getItem(LAST_PAGE_KEY); } catch { return null; }
}
